import { DateObject } from '@adotel/components/build/helpers/date'
import { Paper, Tooltip, Typography } from '@material-ui/core'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import momentJalaali from 'moment-jalaali'
import { useEffect, useState } from 'react'

import { formatRange } from '../../../helpers/date'
import { PaymentTermAssignmentRow } from '../../../types/walletPaymentTerm'
import useStyles from './styles/AssignmentRow.styles'

interface AssignmentRowProps {
  assignment: PaymentTermAssignmentRow
  range: 'YEAR' | 'THREE_MONTH' | 'MONTH'
  calendarDate: DateObject // Start date of the visible calendar range
  selectedRangeForAssignments: DateObject
}

const AssignmentRow: React.FC<AssignmentRowProps> = ({
  assignment,
  range,
  calendarDate,
  selectedRangeForAssignments
}) => {
  const classes = useStyles()
  const [hasEmptyAtStart, sethasEmptyAtStart] = useState<boolean>(false)
  const [hasEmptyAtEnd, sethasEmptyAtEnd] = useState<boolean>(false)
  const [gridTemplate, setGridTemplate] = useState<string>('')
  const lastAssignmentEnd =
    assignment.assignments.length > 0
      ? assignment.assignments
          .map((assn) =>
            momentJalaali(assn.assignmentTimeRange.to, 'YYYY-MM-DD')
          )
          .reduce((max, date) => (date.isAfter(max) ? date : max))
      : null // If no assignments, default to null

  // Helper function to calculate the fraction width for each assignment
  const calculateFractions = () => {
    let calculatedFractions: number[] = []
    let totalFraction = 0

    const totalUnits = range === 'YEAR' ? 365 : range === 'THREE_MONTH' ? 3 : 31
    const visibleStart = momentJalaali(
      calendarDate.gregorianStartDate,
      'YYYY-MM-DD'
    )
    const visibleEnd = momentJalaali(
      calendarDate.gregorianEndDate,
      'YYYY-MM-DD'
    )

    let isTheFirstInRange = true

    // Filter assignments to include only those within the visible range
    const visibleAssignments = assignment.assignments.filter((assn) => {
      const start = momentJalaali(assn.assignmentTimeRange.from, 'YYYY-MM-DD')
      const end = momentJalaali(assn.assignmentTimeRange.to, 'YYYY-MM-DD')
      return !(end.isBefore(visibleStart) || start.isAfter(visibleEnd))
    })

    visibleAssignments.forEach((assn) => {
      const start = momentJalaali(assn.assignmentTimeRange.from, 'YYYY-MM-DD')
      const end = momentJalaali(assn.assignmentTimeRange.to, 'YYYY-MM-DD')

      const effectiveStart = start.isBefore(visibleStart) ? visibleStart : start
      const effectiveEnd = end.isAfter(visibleEnd) ? visibleEnd : end

      let fraction

      if (range === 'YEAR') {
        if (start.isAfter(visibleStart) && isTheFirstInRange) {
          const monthsSpanForEmpty =
            Math.min(start.diff(visibleStart, 'days', true), totalUnits) + 1
          const fractionForEmpty = monthsSpanForEmpty / totalUnits
          calculatedFractions.push(fractionForEmpty)
          totalFraction += fractionForEmpty
          sethasEmptyAtStart(true)
        } else if (isTheFirstInRange) {
          sethasEmptyAtStart(false)
        }
        const monthsSpan =
          Math.min(
            effectiveEnd.diff(effectiveStart, 'days', true),
            totalUnits
          ) + 1

        fraction = monthsSpan / totalUnits
        isTheFirstInRange = false
      } else if (range === 'THREE_MONTH') {
        if (start.isAfter(visibleStart) && isTheFirstInRange) {
          const daysSpanForEmpty =
            Math.min(start.diff(visibleStart, 'days', true), 90) + 1

          const fractionForEmpty = daysSpanForEmpty / 90
          calculatedFractions.push(fractionForEmpty)

          totalFraction += fractionForEmpty
          sethasEmptyAtStart(true)
        } else if (isTheFirstInRange) {
          sethasEmptyAtStart(false)
        }
        const daysSpan =
          Math.min(effectiveEnd.diff(effectiveStart, 'days', true), 90) + 1
        fraction = daysSpan / 90
        isTheFirstInRange = false
      } else {
        if (start.isAfter(visibleStart) && isTheFirstInRange) {
          const daysSpanForEmpty = Math.min(
            start.diff(visibleStart, 'days', true),
            31
          )

          const fractionForEmpty = daysSpanForEmpty / 31
          calculatedFractions.push(fractionForEmpty)
          totalFraction += fractionForEmpty
          sethasEmptyAtStart(true)
        } else if (isTheFirstInRange) {
          sethasEmptyAtStart(false)
        }

        const finalEnd =
          effectiveEnd === visibleEnd ? effectiveEnd : end.add(1, 'days')

        let daysSpan = Math.min(finalEnd.diff(effectiveStart, 'days', true), 31)
        daysSpan = daysSpan === 0 ? 1 : daysSpan
        fraction = daysSpan / 31
        isTheFirstInRange = false
      }

      calculatedFractions.push(fraction)
      totalFraction += fraction
    })

    if (totalFraction < 1) {
      calculatedFractions.push(1 - totalFraction)
      sethasEmptyAtEnd(true)
    } else {
      sethasEmptyAtEnd(false)
    }

    return calculatedFractions.map((frac) => `${frac * totalUnits}fr`).join(' ')
  }

  useEffect(() => {
    const newGridTemplate = calculateFractions()
    setGridTemplate(newGridTemplate)
  }, [
    range,
    assignment,
    calendarDate.gregorianStartDate,
    calendarDate.gregorianEndDate
  ]) // Re-run when range or dates change

  const convertFrStringToArrayWithSum = (
    input: string
  ): { numbers: number[]; sum: number } => {
    const numbers = input
      .split(' ') // Split by space
      .map((value) => parseFloat(value.replace('fr', ''))) // Remove "fr" and convert to number
      .filter((num) => !isNaN(num)) // Ensure valid numbers

    const sum = numbers.reduce((acc, num) => acc + num, 0) // Calculate sum

    return { numbers, sum }
  }

  return (
    <div className={classes.rowContainer}>
      <div className={classes.entityNameContainer}>
        <Typography variant="h5" className={classes.entityName}>
          {assignment.entity.entityName}
        </Typography>
      </div>
      <div
        className={classes.assignmentCells}
        style={{ gridTemplateColumns: gridTemplate }}>
        {hasEmptyAtStart && <div className={classes.emptyCell}></div>}
        {assignment.assignments
          .filter((assn) => {
            const start = momentJalaali(
              assn.assignmentTimeRange.from,
              'YYYY-MM-DD'
            )
            const end = momentJalaali(assn.assignmentTimeRange.to, 'YYYY-MM-DD')
            const calendarStart = momentJalaali(
              calendarDate.gregorianStartDate,
              'YYYY-MM-DD'
            )
            const calendarEnd = momentJalaali(
              calendarDate.gregorianEndDate,
              'YYYY-MM-DD'
            )

            return (
              end.isSameOrAfter(calendarStart) && // Changed from isAfter to isSameOrAfter
              start.isSameOrBefore(calendarEnd) // Changed from isBefore to isSameOrBefore
            )
          })
          .map((assn, index) => {
            const start = momentJalaali(
              assn.assignmentTimeRange.from,
              'YYYY-MM-DD'
            )
            const end = momentJalaali(assn.assignmentTimeRange.to, 'YYYY-MM-DD')

            const startsBeforeVisible = start.isBefore(
              momentJalaali(calendarDate.gregorianStartDate, 'YYYY-MM-DD')
            )
            const endsAfterVisible = end.isAfter(
              momentJalaali(calendarDate.gregorianEndDate, 'YYYY-MM-DD')
            )
            const termNameLength = assn.paymentTermName.length
            const dateRangeLength = formatRange(
              assn.assignmentTimeRange.from,
              assn.assignmentTimeRange.to
            ).length

            const max = Math.max(termNameLength, dateRangeLength)
            const maxPercent = max / 220
            const result = convertFrStringToArrayWithSum(gridTemplate)
            const idx =
              result.numbers[index + (hasEmptyAtStart ? 1 : 0)] / result.sum
            const isSmall = maxPercent > idx // ✅ Adjust threshold as needed

            return (
              <Tooltip
                key={index}
                title={
                  isSmall ? (
                    <>
                      <Typography variant="body2">
                        {assn.paymentTermName}
                      </Typography>
                      <Typography variant="body2">
                        {assn.displayPaymentTermCode}
                      </Typography>
                      <Typography variant="body2">
                        {formatRange(
                          assn.assignmentTimeRange.from,
                          assn.assignmentTimeRange.to
                        )}
                      </Typography>
                    </>
                  ) : (
                    ''
                  )
                }
                arrow>
                <Paper
                  className={[
                    classes.assignmentCell,
                    startsBeforeVisible ? classes.startsBefore : '',
                    endsAfterVisible ? classes.endsAfter : ''
                  ].join(' ')}
                  // style={{ visibility: isSmall ? 'hidden' : 'visible' }} // Hide content if small
                >
                  {!isSmall && (
                    <>
                      <Typography
                        variant="subtitle2"
                        className={classes.termName}>
                        {assn.paymentTermName}
                      </Typography>
                      <Typography variant="body2" className={classes.termCode}>
                        {assn.displayPaymentTermCode}
                      </Typography>
                      <Typography variant="body1" className={classes.dateRange}>
                        <CalendarTodayIcon className={classes.calendarIcon} />
                        {formatRange(
                          assn.assignmentTimeRange.from,
                          assn.assignmentTimeRange.to
                        )}
                      </Typography>
                    </>
                  )}
                </Paper>
              </Tooltip>
            )
          })}
        {/* Empty cell for remaining space */}
        {hasEmptyAtEnd &&
          lastAssignmentEnd?.format('YYYY-MM-DD') !==
            selectedRangeForAssignments.gregorianEndDate && (
            <div className={classes.emptyCell}></div>
          )}
      </div>
    </div>
  )
}

export default AssignmentRow
